/**
 * Pure validatie-functies voor mutatieverzoeken (E2 Selfservice).
 *
 * - NL-postcode: 4 cijfers + spatie? + 2 letters (case-insensitive).
 * - IBAN: alleen NL-vorm wordt structureel gevalideerd via mod-97 over de
 *   verplaatste BBAN. We accepteren alle landen-prefixen die structureel
 *   passen (lengtes uit IBAN-spec voor de meest gebruikte landen).
 * - NL-telefoon: `+31` of `0` gevolgd door 9 cijfers, eerste cijfer 1-9.
 * - Email: pragmatisch RFC-light pattern (geen volledig RFC 5322).
 *
 * Bron: spec E2 Selfservice + algemeen-bekende validatieregels.
 */

import type { MutatieType } from '@streep/types'

const POSTCODE_RE = /^\d{4}\s?[A-Z]{2}$/i
// `+31` of `0`, dan een cijfer 1-9, dan 8 cijfers (NL nummerplan).
const TEL_RE = /^(\+31|0)[1-9]\d{8}$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** NL-postcode in formaat 1234AB of 1234 AB (case-insensitive). */
export function isGeldigeNlPostcode(value: string): boolean {
  if (typeof value !== 'string') return false
  return POSTCODE_RE.test(value.trim())
}

/**
 * IBAN-validatie via het standaard mod-97-algoritme.
 *
 * Stappen:
 *   1. Verwijder spaties + naar uppercase.
 *   2. Lengte-check (minimaal 15, maximaal 34 — IBAN-spec).
 *   3. Verplaats de eerste 4 chars naar het einde.
 *   4. Vervang elke letter door zijn alfabetpositie + 9 (A=10, …, Z=35).
 *   5. Check `BigInt(rearrangedNumeric) % 97n === 1n`.
 *
 * Letten we op landenlengte (NL=18, BE=16, DE=22, …)? Voor v1 niet — de
 * mod-97-check vangt verreweg de meeste typo's op. Per-land lengte-check
 * kan later toegevoegd worden zonder breaking change.
 */
export function isGeldigeIban(value: string): boolean {
  if (typeof value !== 'string') return false
  const cleaned = value.replace(/\s+/g, '').toUpperCase()
  if (cleaned.length < 15 || cleaned.length > 34) return false
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(cleaned)) return false

  // Verplaats eerste 4 naar einde.
  const rearranged = cleaned.slice(4) + cleaned.slice(0, 4)

  // Bouw numeric string: A=10, B=11, …, Z=35.
  let numeric = ''
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0)
    if (code >= 48 && code <= 57) {
      numeric += ch
    } else if (code >= 65 && code <= 90) {
      numeric += String(code - 55)
    } else {
      return false
    }
  }

  try {
    // BigInt literals (97n / 1n) vereisen ES2020+; sommige consumers
    // (apps/backoffice) targeten ES2017. Gebruik daarom de constructor.
    return BigInt(numeric) % BigInt(97) === BigInt(1)
  } catch {
    return false
  }
}

/** NL-telefoonnummer (+31… of 0…). Spaties/streepjes worden eerst gestript. */
export function isGeldigNlTelefoon(value: string): boolean {
  if (typeof value !== 'string') return false
  const cleaned = value.replace(/[\s\-().]/g, '')
  return TEL_RE.test(cleaned)
}

/** Pragmatische e-mail-check — geen volledige RFC 5322, wel goed genoeg. */
export function isGeldigEmail(value: string): boolean {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (trimmed.length === 0 || trimmed.length > 254) return false
  return EMAIL_RE.test(trimmed)
}

// ─── Payload-validatie per mutatie-type ──────────────────────────────────────

export interface PayloadValidationResult {
  valid: boolean
  /** Verplichte velden die ontbreken of leeg zijn. */
  missing: string[]
  /** Velden die wél aanwezig zijn maar het format-check niet halen. */
  invalid: string[]
}

const REQUIRED_FIELDS: Record<MutatieType, readonly string[]> = {
  ADRES: ['street', 'houseNumber', 'postalCode', 'city'],
  BANK: ['iban', 'bankTnvNaam'],
  NOODCONTACT: ['noodcontactNaam', 'noodcontactRelatie', 'noodcontactTelefoon'],
  TELEFOON: ['phone'],
  EMAIL: ['email'],
  VERLOFOPBOUW: ['atvOpbouw', 'tvtWerk', 'tvtReis'],
  // Batch-6 verlof — kind toevoegen (relatie heeft een default).
  KIND: ['naam', 'geboortedatum'],
  PRIVE_TELEFOON: ['privatePhone'],
  PRIVE_EMAIL: ['privateEmail'],
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0
}

/**
 * Controleert of een veld aanwezig is in de payload.
 * Booleans (`true`/`false`) worden als aanwezig beschouwd; voor strings geldt
 * de strikte `isNonEmptyString`-check (niet-leeg, niet-whitespace).
 */
function isPresent(v: unknown): boolean {
  if (typeof v === 'boolean') return true
  return isNonEmptyString(v)
}

/**
 * Validate a mutatie-payload tegen het verwachte format voor het gegeven type.
 *
 * Retourneert verzamelde lijsten i.p.v. eerste-fout-gooien zodat de UI alle
 * problemen tegelijk kan tonen. Aanwezigheid + non-empty wordt apart van
 * format-check getoetst.
 */
export function valideerMutatiePayload(
  type: MutatieType,
  payload: Record<string, unknown>,
): PayloadValidationResult {
  const required = REQUIRED_FIELDS[type]
  const missing: string[] = []
  const invalid: string[] = []

  for (const field of required) {
    if (!isPresent(payload[field])) {
      missing.push(field)
    }
  }

  // Format-check alleen voor velden die wél aanwezig zijn (anders dubbele fout).
  if (type === 'ADRES' && isNonEmptyString(payload.postalCode)) {
    if (!isGeldigeNlPostcode(payload.postalCode)) invalid.push('postalCode')
  }

  if (type === 'BANK' && isNonEmptyString(payload.iban)) {
    if (!isGeldigeIban(payload.iban)) invalid.push('iban')
  }

  if (type === 'NOODCONTACT' && isNonEmptyString(payload.noodcontactTelefoon)) {
    if (!isGeldigNlTelefoon(payload.noodcontactTelefoon)) invalid.push('noodcontactTelefoon')
  }

  if (type === 'TELEFOON' && isNonEmptyString(payload.phone)) {
    if (!isGeldigNlTelefoon(payload.phone)) invalid.push('phone')
  }

  if (type === 'EMAIL' && isNonEmptyString(payload.email)) {
    if (!isGeldigEmail(payload.email)) invalid.push('email')
  }

  if (type === 'PRIVE_TELEFOON' && isNonEmptyString(payload.privatePhone)) {
    if (!isGeldigNlTelefoon(payload.privatePhone)) invalid.push('privatePhone')
  }

  if (type === 'PRIVE_EMAIL' && isNonEmptyString(payload.privateEmail)) {
    if (!isGeldigEmail(payload.privateEmail)) invalid.push('privateEmail')
  }

  return {
    valid: missing.length === 0 && invalid.length === 0,
    missing,
    invalid,
  }
}
